import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import TickerWithName from "../components/TickerWithName";
import api from "../utils/api";
import { QuickTradeEquityButton } from "../components/QuickTradeButtons";


// local helpers (you can swap for your shared utils if you prefer)
const money2 = (v) => (v == null || v === '' ? '0.00' : (Number.isFinite(Number(v)) ? Number(v).toFixed(2) : String(v)));
const pctChange = (a, b) => {
  const x = Number(a), y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y) || x === 0) return null;
  return ((y - x) / x) * 100;
};
const EstExitLine = ({ entry, exit }) => {
  const a = Number(entry), b = Number(exit);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return <div className="text-sm">Est Exit: ${money2(exit)}</div>;
  const pct = ((b - a) / a) * 100;
  const good = pct >= 0;
  return (
    <div className="text-sm">
      Est Exit: ${money2(exit)}{" "}
      <span className={`ml-1 px-2 py-0.5 rounded text-xs font-semibold ${good ? "bg-green-600" : "bg-red-600"}`}>
        {good ? "+" : ""}{pct.toFixed(2)}%
      </span>
    </div>
  );
};

const API = "https://mystockpicks.onrender.com";

export default function ShortSqueezes() {
  const [period, setPeriod] = useState("day"); // "day" | "week"
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();


    useEffect(() => {
        const ctrl = new AbortController();
        let cancelled = false;

        (async () => {
            try {
                setLoading(true);
                setErr("");

                const fastUrl = `/api/short-squeezes?period=${period}&limit=10&fast=1`;
                const fullUrl = `/api/short-squeezes?period=${period}&limit=10`;

                // try fast/cached universe first
                let { data } = await api.get(fastUrl, { timeout: 45000, signal: ctrl.signal });
                if (!data?.results?.length) {
                    // fall back to full scan (will usually be cached on 2nd hit)
                    ({ data } = await api.get(fullUrl, { timeout: 45000, signal: ctrl.signal }));
                }

                if (!cancelled) setRows(data?.results || []);
            } catch (e) {
                if (axios.isCancel?.(e) || e?.name === "CanceledError") return;
                if (!cancelled) setErr("Failed to fetch short squeezes.");
                console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            ctrl.abort();
        };
    }, [period]);


  return (
    <div className="p-4 sm:p-5">
      <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6 text-blue-400 drop-shadow">Short Squeeze Radar</h2>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          className={`px-4 py-2 rounded-xl font-semibold ${period === "day" ? "bg-blue-600 ring-2 ring-blue-400" : "bg-gray-700 hover:bg-blue-500"}`}
          onClick={() => setPeriod("day")}
        >
          Today
        </button>
        <button
          className={`px-4 py-2 rounded-xl font-semibold ${period === "week" ? "bg-blue-600 ring-2 ring-blue-400" : "bg-gray-700 hover:bg-blue-500"}`}
          onClick={() => setPeriod("week")}
        >
          This Week
        </button>
      </div>

      {loading && <div className="text-gray-400">Scanning…</div>}
      {err && <div className="text-red-400">{err}</div>}

      <div className="space-y-3">
        {rows.map((r, idx) => (
          <div
            key={idx}
            className="bg-gray-700 p-3 sm:p-4 rounded-lg shadow hover:bg-gray-600 cursor-pointer space-y-1"
            onClick={() => navigate(`/chart/${r.ticker}`)}
          >
            <div className="flex items-center text-lg sm:text-xl font-semibold">
              <TickerWithName symbol={r.ticker} />
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded bg-purple-600">
                Score {r.squeezeScore}
              </span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
              <div>Short % Float: {r.shortPercentFloat == null ? "N/A" : `${r.shortPercentFloat}%`}</div>
              <div>Days to Cover: {r.daysToCover == null ? "N/A" : r.daysToCover}</div>
              <div>Volume Spike: ×{r.volumeSpike}</div>
              <div>{period === "week" ? "5-day Change" : "1-day Change"}: {r.changePct}%</div>
            </div>

            <div className="text-sm">Entry: ${money2(r.entryPrice)}</div>
            <EstExitLine entry={r.entryPrice} exit={r.estimatedExit} />

            <div className="text-sm text-gray-400">{r.reason}</div>
            <QuickTradeEquityButton ticker={r.ticker} />

          </div>
        ))}

        {!loading && rows.length === 0 && <div className="text-gray-400">No candidates found right now.</div>}
      </div>
    </div>
  );
}

